import { AlertAddToListButton } from '@/components/AlertAddToListButton';
import { OutcomeBadge } from '@/components/DataTable/alerts/CellRenderers';
import { SeverityBadge } from '@/components/policylist/components/SeverityBadge';
import { ScanDetailFormattedText } from '@/components/ScanDetailFormattedText';
import { Badge } from '@/components/ui/badge';
import type { AddFromAlertScope } from '@/hooks/api/useAddPolicyExceptionFromAlert';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { cn } from '@/lib/utils';
import type { GroupedAlert } from '@kirin/api-client/contracts/alerts';
import { ExtensionFindingType } from '@kirin/api-client/contracts/analyses';
import {
  formatAlertType,
  formatMcpGatewaySections,
  formatSubPolicyDescription,
  formatSubPolicyLabel,
  formatSubPolicySections,
} from '@kirin/api-client/helpers/alerts';
import { hasKnownEntityName } from '@kirin/api-client/helpers/source-details';
import { AlertOsvComponentSection } from './AlertOsvComponentSection';
import { toPlatformId } from '@kirin/api-client/contracts/platform-registry';
import type { ReactElement } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { AlertDescription } from './AlertDescription';
import { AlertSourceDetails } from './AlertSourceDetails';
import { AlertSecurityGroupDetail } from './AlertSecurityGroupDetail';
import { AlertSecretDetails } from './AlertSecretDetails';
import { getMcpLocationEvidence } from './alert-location';
import { hasSecretDetails } from './AlertSecretDetails.helpers';
import { AggregateScoreChip } from './AggregateScoreChip';
import {
  EXTENSION_SCAN_RESULT_FROM_ALERTS,
  buildExtensionScanResultLink,
} from './extension-scan-result-link';
import { SecretConfidenceBadge } from './SecretConfidenceBadge';
import { WhyThisFired } from './WhyThisFired';

interface AlertDetailCardProps {
  alert: Pick<
    GroupedAlert,
    | 'type'
    | 'severity'
    | 'description'
    | 'location'
    | 'filePath'
    | 'subPolicyKey'
    | 'sourceDetails'
    | 'scoreBreakdown'
    | 'outcome'
    // KIR-3765 secret-detection enrichment (representative on grouped alerts).
    | 'engine'
    | 'ruleId'
    | 'ruleName'
    | 'entropy'
    | 'toolName'
    | 'sessionId'
    | 'workspaceName'
    | 'matchContext'
    | 'project'
    | 'clientTypes'
  > &
    // KIR-3800: optional because the field is flag-gated on the org alerts API
    // and absent entirely on the personal-dashboard alert row. Absent → no
    // security-group row, which is also the right answer there.
    Partial<Pick<GroupedAlert, 'securityGroups'>>;
  className?: string;
  /** Org scope for scan-result links; falls back to the route param when omitted. */
  organizationId?: string;
  /**
   * When provided (together with {@link alertKey}), the card header surfaces the
   * "Add to Allow/Block List" action (KIR-3558) for the alert's entity, placed
   * top-right next to the severity badge. Omitted where the caller can't resolve
   * a scope, in which case no action is shown.
   */
  scope?: AddFromAlertScope;
  /** Alert key, threaded into the add-to-list action. */
  alertKey?: string;
}

/**
 * Renders alert type, severity, description, and optional location in a bordered card.
 * Shared between the AlertsTable expanded row and the AlertOccurrencesPanel modal layout.
 *
 * KIR-2381: revamp on -> centralized SUB_POLICY_DISPLAY label + description;
 * off -> legacy `alert.type` / `alert.description` for byte-identical off-state.
 */
export function AlertDetailCard({
  alert,
  className,
  organizationId: organizationIdProp,
  scope,
  alertKey,
}: AlertDetailCardProps): ReactElement {
  const routerLocation = useLocation();
  const { organizationId: organizationIdFromRoute } = useParams<{
    organizationId?: string;
  }>();
  const organizationId = organizationIdProp ?? organizationIdFromRoute;
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.ALERTS_REVAMP,
    flagsmithFeatureFlags.ALERT_SOURCE_DETAILS,
    flagsmithFeatureFlags.CAPABILITY_DETECTION,
    flagsmithFeatureFlags.SECRET_DETECTION_DETAILS,
  ]);
  const revampEnabled = flags[flagsmithFeatureFlags.ALERTS_REVAMP] === true;
  // KIR-4190: OSV fields live on the extension and mcp variants only.
  const osvComponent =
    alert.sourceDetails?.kind === 'extension' ||
    alert.sourceDetails?.kind === 'mcp'
      ? alert.sourceDetails.osvComponent
      : undefined;
  const osvAdvisoryId =
    alert.sourceDetails?.kind === 'extension' ||
    alert.sourceDetails?.kind === 'mcp'
      ? alert.sourceDetails.osvId
      : undefined;
  const sourceDetailsEnabled =
    flags[flagsmithFeatureFlags.ALERT_SOURCE_DETAILS] === true;
  const capabilityDetectionEnabled =
    flags[flagsmithFeatureFlags.CAPABILITY_DETECTION] ?? false;
  const secretDetailsEnabled =
    flags[flagsmithFeatureFlags.SECRET_DETECTION_DETAILS] === true;
  const secretDetails = {
    engine: alert.engine,
    ruleId: alert.ruleId,
    ruleName: alert.ruleName,
    entropy: alert.entropy,
    toolName: alert.toolName,
    sessionId: alert.sessionId,
    workspaceName: alert.workspaceName,
    matchContext: alert.matchContext,
    project: alert.project,
    // Only trust a single unambiguous client as the session's platform, then
    // narrow it to a canonical registry PlatformId; a grouped alert spanning
    // multiple clients (or an unregistered client) leaves the transcript
    // location generic rather than guessing.
    platform:
      alert.clientTypes?.length === 1
        ? toPlatformId(alert.clientTypes[0])
        : undefined,
  };
  const label = revampEnabled
    ? formatSubPolicyLabel(alert.subPolicyKey, alert.type)
    : formatAlertType(alert.type);
  const description = revampEnabled
    ? formatSubPolicyDescription(
        alert.subPolicyKey,
        alert.type,
        alert.description
      )
    : alert.description;
  const descriptionSections = revampEnabled
    ? // KIR-3287: the gateway's mcp:<surface>:<tool> sentinel lives in filePath,
      // not location — location is the issue-level finding location (e.g.
      // `Text: "ghp_****"`). Non-gateway and non-secret alerts get null here and
      // fall back to their own policy sections; the sentinel check is intrinsic.
      (formatMcpGatewaySections(alert.filePath ?? '', alert.type) ??
      formatSubPolicySections(
        alert.subPolicyKey,
        alert.type,
        alert.description,
        alert.location
      ))
    : null;
  const isExtensionSecurityAlert =
    alert.sourceDetails?.kind === 'extension' ||
    alert.scoreBreakdown !== undefined ||
    alert.type
      .toLowerCase()
      .includes(ExtensionFindingType.ExcessivePermissions);

  // KIR-4175: for an MCP tool-description alert, render the Location as the
  // tool/server identity + the description WINDOW around the match (highlighted)
  // instead of the head-truncated preview (which never contains the match). This
  // is display-only — the stored/hashed `location` is untouched, so the
  // finding_key is unaffected. `null` for every other alert → raw location.
  const mcpLocation = getMcpLocationEvidence(
    alert.sourceDetails,
    alert.location
  );
  const scanResultLink =
    capabilityDetectionEnabled && isExtensionSecurityAlert
      ? buildExtensionScanResultLink({
          location: alert.location,
          sourceDetails: alert.sourceDetails,
          organizationId,
          from: EXTENSION_SCAN_RESULT_FROM_ALERTS,
          returnTo: `${routerLocation.pathname}${routerLocation.search}`,
        })
      : null;
  return (
    <div
      className={cn(
        'rounded-lg border bg-white p-3 dark:bg-slate-800',
        className
      )}
    >
      <div className="flex flex-wrap items-center gap-2 mb-2">
        {capabilityDetectionEnabled && isExtensionSecurityAlert ? (
          <Badge variant="outline">{label}</Badge>
        ) : (
          <span className="font-medium text-sm">{label}</span>
        )}
        <SeverityBadge riskLevel={alert.severity} showIcon />
        {capabilityDetectionEnabled && isExtensionSecurityAlert && (
          <>
            <OutcomeBadge outcome={alert.outcome} />
            <AggregateScoreChip score={alert.scoreBreakdown?.score} />
          </>
        )}
        {secretDetailsEnabled && typeof alert.entropy === 'number' && (
          <SecretConfidenceBadge entropy={alert.entropy} />
        )}
        {scope && alertKey && (
          <AlertAddToListButton
            scope={scope}
            sourceDetails={alert.sourceDetails}
            alertKey={alertKey}
            analyticsContext={{ severity: alert.severity, type: alert.type }}
            className="ml-32 shrink-0"
          />
        )}
      </div>
      <AlertDescription
        sections={descriptionSections}
        fallbackDescription={description}
        className="text-sm text-muted-foreground mb-1"
      />
      <AlertSecurityGroupDetail securityGroups={alert.securityGroups} />
      {(mcpLocation || alert.location) && (
        <div className="mt-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Location
          </p>
          <div className="mt-1 text-xs font-mono bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded min-w-0 max-w-full break-words">
            {mcpLocation ? (
              <>
                {mcpLocation.identity && <div>{mcpLocation.identity}</div>}
                <div className="mt-2 text-muted-foreground">
                  Tool description:
                </div>
                <div className="break-words whitespace-pre-wrap">
                  {mcpLocation.before}
                  <mark className="rounded bg-amber-100 px-1 py-0.5 font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
                    {mcpLocation.match}
                  </mark>
                  {mcpLocation.after}
                </div>
              </>
            ) : (
              <ScanDetailFormattedText location={alert.location ?? ''} />
            )}
          </div>
        </div>
      )}
      {capabilityDetectionEnabled && isExtensionSecurityAlert && (
        <WhyThisFired type={alert.type} scoreBreakdown={alert.scoreBreakdown} />
      )}
      {/*
        KIR-4190: a sibling of AlertSourceDetails, not a child, on purpose —
        that panel is also gated on `hasKnownEntityName`, and an MCP dependency
        finding with no parseable config has an Unknown Name, which would hide
        the CVE/CVSS too. Same `alert_source_details` flag either way.
      */}
      {sourceDetailsEnabled && osvComponent && (
        <AlertOsvComponentSection
          osvComponent={osvComponent}
          {...(osvAdvisoryId ? { osvId: osvAdvisoryId } : {})}
        />
      )}
      {alert.sourceDetails?.kind === 'pii' ? (
        // PII redacted-content sample (KIR-3034): shown whenever present — it
        // carries no Allow/Block entity, so the entity-name gate doesn't apply.
        <AlertSourceDetails sourceDetails={alert.sourceDetails} />
      ) : (
        sourceDetailsEnabled &&
        alert.sourceDetails &&
        hasKnownEntityName(alert.sourceDetails) && (
          <AlertSourceDetails sourceDetails={alert.sourceDetails} />
        )
      )}
      {secretDetailsEnabled && hasSecretDetails(secretDetails) && (
        <AlertSecretDetails
          details={secretDetails}
          organizationId={organizationId}
        />
      )}
      {scanResultLink && (
        <Link
          to={scanResultLink}
          className="mt-3 inline-flex text-sm font-medium text-primary underline-offset-4 hover:underline"
        >
          View full scan result →
        </Link>
      )}
    </div>
  );
}

AlertDetailCard.displayName = 'AlertDetailCard';
